import { FC, ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useClickOutOfElement } from "../../../../../custom-hooks/base-hooks/useClickOutOfElement";
import { WorkSortingFields } from "../../../../../domain/enums/entity-fields/WorkSortingFields";
import { FormikProps } from "formik";
import { SortingOrder } from "../../../../../domain/enums/common/SortingOrderEnum";
import DropdownArrowSVG from "../../../svg/functional/DropdownArrowSVG";
import { WorkSearchSortForm } from "../../../../../domain/models/dto/WorkDTO";
import CheckMarkIconSVG from "../../../svg/CheckMarkIconSVG";
import useScreenSize from "../../../../../custom-hooks/base-hooks/useScreenSize";

interface WorkSortingTooltipProps {
    form: FormikProps<WorkSearchSortForm>;
    sortingType: WorkSortingFields;
    currentSortingType: WorkSortingFields;
    openingHandler: (value: WorkSortingFields) => void;
    setSortingType: (value: WorkSortingFields) => void;
    buttonElement: ReactNode;
}

const WorkSortingTooltip: FC<WorkSortingTooltipProps> = (props) => {
    const { t } = useTranslation();
    const { moreOrEqual650 } = useScreenSize();
    const tooltipRef = useRef(null);
    const sortingHandler = async (sort: WorkSortingFields, sortingOrder: SortingOrder) => {
        await props.form.setFieldValue(sort, sortingOrder);
        await props.form.submitForm();
        props.setSortingType(null);
    };
    const activeTooltip = props.sortingType === props.currentSortingType;
    const isDefault = props.form.values[props.sortingType] === SortingOrder.DEFAULT;
    const isAsc = props.form.values[props.sortingType] === SortingOrder.ASC;
    const isDesc = props.form.values[props.sortingType] === SortingOrder.DESC;
    useClickOutOfElement(tooltipRef, () => {
        activeTooltip && props.setSortingType(null);
    });

    return (
        <div
            ref={tooltipRef}
            onClick={() => props.openingHandler(props.sortingType)}
            className="relative flex items-center cursor-pointer group"
        >
            <div className={`flex items-center ${isAsc || isDesc ? "*:text-textWhite" : ""}`}>
                {props.buttonElement}
                {moreOrEqual650 && (
                    <DropdownArrowSVG
                        className={`text-textFaded group-hover:text-textWhite w-[21px] dmax2:w-4 transition duration-300 ${
                            activeTooltip ? "rotate-[0deg]" : "rotate-[-90deg]"
                        }`}
                    />
                )}
            </div>
            <div
                className={`absolute top-10 z-10 flex flex-col gap-3 dmax2:gap-2 items-center justify-center bg-backgroundInput w-[270px] h-[156px] dmin2:py-4 dmax2:pt-4 px-6 rounded-[7px] transition duration-300 ${
                    activeTooltip ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
            >
                <div
                    className={`flex justify-center H16 dmax2:H14 text-textWhite text-center w-[214px] h-[32px] hover:font-[500] border-b border-b-textFaded cursor-pointer transition-all duration-300`}
                    onClick={() => sortingHandler(props.sortingType, SortingOrder.DEFAULT)}
                >
                    {t(`sortingOrder.${SortingOrder.DEFAULT}`)}
                    {isDefault && <CheckMarkIconSVG className="ml-2.5" />}
                </div>
                <div
                    className={`flex justify-center H16 dmax2:H14 text-textWhite text-center w-[214px] h-[32px] hover:font-[500] border-b border-b-textFaded cursor-pointer transition-all duration-300`}
                    onClick={() => sortingHandler(props.sortingType, SortingOrder.DESC)}
                >
                    {t(`catalog.sort.order.${props.sortingType}.${SortingOrder.DESC}`)}
                    {isDesc && <CheckMarkIconSVG className="ml-2.5" />}
                </div>
                <div
                    className={`flex justify-center H16 dmax2:H14 text-textWhite text-center w-[214px] h-[32px] hover:font-[500] cursor-pointer transition-all duration-300`}
                    onClick={() => sortingHandler(props.sortingType, SortingOrder.ASC)}
                >
                    {t(`catalog.sort.order.${props.sortingType}.${SortingOrder.ASC}`)}
                    {isAsc && <CheckMarkIconSVG className="ml-2.5" />}
                </div>
            </div>
        </div>
    );
};

export default WorkSortingTooltip;
